import { useState } from 'react';
import { DisclosureRow, AccentCard, Eyebrow } from './design';
import { useAppModeStore } from '../store/useAppModeStore';

const LS_KEY = 'vehicletco-help-panel-dismissed';

function isNewUser(): boolean {
  return localStorage.getItem(LS_KEY) === null;
}

export default function HelpPanel() {
  const [expanded, setExpanded] = useState<boolean>(isNewUser);
  const isFleet = useAppModeStore((s) => s.isFleet);

  function handleToggle(next: boolean) {
    setExpanded(next);
    if (!next) {
      localStorage.setItem(LS_KEY, '1');
    } else {
      localStorage.removeItem(LS_KEY);
    }
  }

  return (
    <AccentCard padding="14px 20px" style={{ marginBottom: 18 }}>
      <DisclosureRow
        label="How it works"
        expanded={expanded}
        onToggle={handleToggle}
        labelFontSize={13}
        labelColor="var(--tco-ink-2)"
      >
        <div style={{
          paddingTop: 14,
          borderTop: '1px solid var(--tco-line-soft)',
          fontSize: 14,
          color: 'var(--tco-ink-2)',
          lineHeight: 1.55,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}>
          {isFleet ? <FleetHelpBody /> : <PersonalHelpBody />}
        </div>
      </DisclosureRow>
    </AccentCard>
  );
}

function PersonalHelpBody() {
  return (
    <>
      <div>
        <Eyebrow style={{ marginBottom: 6 }}>Compare the true cost of owning any vehicle</Eyebrow>
        <p style={{ margin: 0 }}>
          Enter two vehicles, set your horizon and assumptions, and see a full 5-year cost
          breakdown — depreciation, fuel, insurance, maintenance, and more. The tool uses Argonne
          National Lab depreciation data and live EIA fuel prices.
        </p>
      </div>

      <div>
        <Eyebrow style={{ marginBottom: 6 }}>Why it surprises people</Eyebrow>
        <p style={{ margin: 0 }}>
          The cheapest car to buy is rarely the cheapest car to own — and the most expensive
          is not always the most expensive to own either. A $47,000 BMW 330i and a $39,000
          Honda Accord Touring Hybrid look close on the sticker. Over five years the BMW
          costs roughly $17,000 more to own. The demo scenario shows exactly how that happens.
        </p>
      </div>

      <div>
        <Eyebrow style={{ marginBottom: 8 }}>Key concepts</Eyebrow>
        <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>
            <dt style={{ display: 'inline', color: 'var(--tco-ink)', fontWeight: 600 }}>Depreciation: </dt>
            <dd style={{ display: 'inline', margin: 0 }}>
              The single largest cost for most vehicles. A car that holds its value well costs
              far less to own than one that drops sharply.
            </dd>
          </div>
          <div>
            <dt style={{ display: 'inline', color: 'var(--tco-ink)', fontWeight: 600 }}>Opportunity Cost: </dt>
            <dd style={{ display: 'inline', margin: 0 }}>
              The return you give up by putting cash into a vehicle instead of investing it.
              A $40,000 cash purchase has a real cost beyond the sticker price.
            </dd>
          </div>
          <div>
            <dt style={{ display: 'inline', color: 'var(--tco-ink)', fontWeight: 600 }}>Emotional Score: </dt>
            <dd style={{ display: 'inline', margin: 0 }}>
              Rate each vehicle on dimensions of personal satisfaction. Move the financial
              and emotional slider to see how your priorities change the recommendation.
            </dd>
          </div>
        </dl>
      </div>

      <div>
        <Eyebrow style={{ marginBottom: 8 }}>How to use it</Eyebrow>
        <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <li>Create a new scenario and add the vehicles you are considering</li>
          <li>Enter purchase price and financing terms</li>
          <li>Fill in operating costs including fuel type and insurance</li>
          <li>Set your emotional scores for each vehicle</li>
          <li>Run the analysis and review the Rankings and TCO Summary</li>
        </ol>
      </div>

      <p style={{ margin: 0, fontSize: 12, color: 'var(--tco-ink-3)', fontStyle: 'italic' }}>
        The demo scenario loads automatically for new users — open it to see a real example before entering your own vehicles.
      </p>
    </>
  );
}

function FleetHelpBody() {
  return (
    <>
      <div>
        <Eyebrow style={{ marginBottom: 6 }}>Model total cost of ownership across your fleet</Eyebrow>
        <p style={{ margin: 0 }}>
          Add vehicles by type and quantity, set vocation and duty cycle, and compute a full
          fleet TCO — including fuel by vocation, Section 179 tax benefit, labor, and cost per
          mile. Compare fleet configurations side by side.
        </p>
      </div>

      <div>
        <Eyebrow style={{ marginBottom: 8 }}>Key concepts</Eyebrow>
        <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>
            <dt style={{ display: 'inline', color: 'var(--tco-ink)', fontWeight: 600 }}>Vocation: </dt>
            <dd style={{ display: 'inline', margin: 0 }}>
              How the vehicle is used (local delivery, regional haul, service, etc.). Drives
              fuel economy assumptions and idle time.
            </dd>
          </div>
          <div>
            <dt style={{ display: 'inline', color: 'var(--tco-ink)', fontWeight: 600 }}>Section 179: </dt>
            <dd style={{ display: 'inline', margin: 0 }}>
              Federal first-year deduction on qualifying business vehicles. The tool computes
              the tax benefit from your marginal rate and business-use percentage.
            </dd>
          </div>
          <div>
            <dt style={{ display: 'inline', color: 'var(--tco-ink)', fontWeight: 600 }}>Cost per mile: </dt>
            <dd style={{ display: 'inline', margin: 0 }}>
              Lifetime TCO divided by total fleet miles — the headline operating efficiency
              metric for fleet planning.
            </dd>
          </div>
        </dl>
      </div>

      <div>
        <Eyebrow style={{ marginBottom: 8 }}>How to use it</Eyebrow>
        <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <li>Create a scenario and add line items by vehicle type and quantity</li>
          <li>Set vocation, duty cycle, and annual miles per vehicle</li>
          <li>Enter purchase price, financing, insurance, and labor inputs</li>
          <li>Configure federal tax inputs (marginal rate, business-use %, Section 179)</li>
          <li>Compare two fleet configurations side by side from the Compare tab</li>
        </ol>
      </div>
    </>
  );
}
